import {
  CollectionCapacityCapabilityResult,
  CollectionDocument,
  CollectionMembershipDocument,
  CollectionRole,
  InventoryItemSort,
  parseCollectionCapacityCapabilityResult,
} from '@qkindex/contracts';
import {
  CollectionInventoryStore,
  InventoryResource,
} from './collectionInventoryStore';
import type { InventoryPage } from '../../repositories/multiCollectionInventoryRepository';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import type { InventoryMetadataCache } from './inventoryMetadataCache';

export const INVENTORY_RECONCILE_MS = 5 * 60_000;
export const INVENTORY_FOCUS_COOLDOWN_MS = 30_000;
export const CAPACITY_CAPABILITY_REFRESH_MS = 60_000;
export type CollectionCapacityCapabilityState =
  | { status: 'inactive' | 'loading' | 'error' }
  | { status: 'ready'; value: CollectionCapacityCapabilityResult };
export interface CapacityContext {
  collectionId: string;
  membershipVersion: number;
  role: CollectionRole;
}
type Next<T> = (value: T | undefined) => void;
interface Source<T> {
  listeners: Set<{ next: Next<T>; error: () => void }>;
  generation: number;
  snapshotGeneration?: number;
  snapshot?: { value: T | undefined } | { error: true };
  stop?: () => void;
}
interface Dependencies {
  membership(
    id: string,
    userId: string,
    next: Next<CollectionMembershipDocument>,
    error: () => void,
  ): () => void;
  collection(
    id: string,
    next: Next<CollectionDocument>,
    error: () => void,
  ): () => void;
  capacity(context: CapacityContext): Promise<unknown>;
  cache?: InventoryMetadataCache;
  record?(
    id: string,
    kind: InventoryResource,
    recordId: string,
  ): Promise<Item | Location | undefined>;
  page?(
    id: string,
    kind: InventoryResource,
    cursor?: string,
    sort?: InventoryItemSort,
  ): Promise<InventoryPage<Item | Location>>;
}
const inactive: CollectionCapacityCapabilityState = { status: 'inactive' };
const loading: CollectionCapacityCapabilityState = { status: 'loading' };
const error: CollectionCapacityCapabilityState = { status: 'error' };
export const INACTIVE_CAPACITY_SNAPSHOT = {
  state: inactive,
  displayState: inactive,
};
interface CapacityEntry {
  context: CapacityContext;
  snapshot: {
    state: CollectionCapacityCapabilityState;
    displayState: CollectionCapacityCapabilityState;
  };
  listeners: Set<() => void>;
  generation: number;
  expiresAt: number;
  pending?: Promise<void>;
  dirty: boolean;
  timer?: ReturnType<typeof setTimeout>;
}

/** One authenticated session. Cached presentation never authorizes mutations. */
export class CollectionDataStore {
  private memberships = new Map<string, Source<CollectionMembershipDocument>>();
  private collections = new Map<string, Source<CollectionDocument>>();
  private capacities = new Map<string, CapacityEntry>();
  private disposed = false;
  private listeningForVisibility = false;
  private inventoryTimer?: ReturnType<typeof setInterval>;
  private visibilityTimer?: ReturnType<typeof setTimeout>;

  readonly inventory: CollectionInventoryStore;
  constructor(
    readonly userId: string,
    private dependencies: Dependencies,
  ) {
    this.inventory = new CollectionInventoryStore({
      membership: (id, next, error) =>
        this.subscribeMembership(id, next, error),
      collection: (id, next, error) =>
        this.subscribeCollection(id, next, error),
      page: (id, kind, cursor, sort) =>
        dependencies.page
          ? sort
            ? dependencies.page(id, kind, cursor, sort)
            : dependencies.page(id, kind, cursor)
          : Promise.reject(new Error('Inventory transport unavailable.')),
      cache: dependencies.cache,
      record: dependencies.record,
      demandChanged: () => this.updateVisibilityListeners(),
    });
  }

  private subscribe<T>(
    sources: Map<string, Source<T>>,
    id: string,
    listen: (next: Next<T>, error: () => void) => () => void,
    next: Next<T>,
    onError: () => void,
  ) {
    if (this.disposed) return () => undefined;
    let source = sources.get(id);
    const observer = { next, error: onError };
    if (!source) {
      source = { listeners: new Set(), generation: 0 };
      sources.set(id, source);
    }
    const snapshotIsCurrent = source.snapshotGeneration === source.generation;
    const restart =
      !source.listeners.size ||
      Boolean(
        snapshotIsCurrent && source.snapshot && 'error' in source.snapshot,
      );
    source.listeners.add(observer);
    if (restart) {
      source.stop?.();
      const generation = ++source.generation;
      const current = source;
      const publish = (snapshot: NonNullable<Source<T>['snapshot']>) => {
        if (this.disposed || current.generation !== generation) return;
        const previous = current.snapshot;
        current.snapshot = snapshot;
        current.snapshotGeneration = generation;
        this.authorityChanged(id, previous, snapshot);
        current.listeners.forEach((listener) => {
          if ('error' in snapshot) listener.error();
          else listener.next(snapshot.value);
        });
      };
      current.stop = listen(
        (value) => publish({ value }),
        () => publish({ error: true }),
      );
    } else {
      // Retain old snapshots for change detection, never for validation of a
      // restarted listener that has not delivered its own snapshot yet.
      if (snapshotIsCurrent && source.snapshot) {
        if ('error' in source.snapshot) onError();
        else next(source.snapshot.value);
      }
    }
    const current = source;
    return () => {
      current.listeners.delete(observer);
      if (!current.listeners.size && current.stop) {
        current.generation += 1;
        current.stop?.();
        current.stop = undefined;
      }
    };
  }

  subscribeMembership = (
    id: string,
    next: Next<CollectionMembershipDocument>,
    error: () => void,
  ) =>
    this.subscribe(
      this.memberships,
      id,
      (next, error) =>
        this.dependencies.membership(id, this.userId, next, error),
      next,
      error,
    );
  subscribeCollection = (
    id: string,
    next: Next<CollectionDocument>,
    error: () => void,
  ) =>
    this.subscribe(
      this.collections,
      id,
      (next, error) => this.dependencies.collection(id, next, error),
      next,
      error,
    );

  private authorityChanged(id: string, previous: unknown, current: unknown) {
    // Revision-only changes do not invalidate capacity; role/lifecycle epochs do.
    const identity = (snapshot: unknown) => {
      const source = snapshot as
        | {
            error?: boolean;
            value?: CollectionMembershipDocument & CollectionDocument;
          }
        | undefined;
      const value = source?.value;
      return JSON.stringify([
        source?.error,
        value?.status,
        value?.role,
        value?.membershipVersion,
        value?.lifecycle,
        value?.lifecycleVersion,
        value?.ownerUserId,
      ]);
    };
    this.capacities.forEach((entry) => {
      if (entry.context.collectionId !== id) return;
      if (!this.allowed(entry.context)) this.fence(entry);
      else if (previous && identity(previous) !== identity(current)) {
        this.fence(entry);
        if (entry.listeners.size) void this.refreshEntry(entry);
      }
    });
  }

  private allowed(context: CapacityContext) {
    if (
      this.disposed ||
      !['owner', 'administrator', 'editor'].includes(context.role)
    )
      return false;
    const membership = this.memberships.get(context.collectionId)?.snapshot;
    if (
      membership &&
      ('error' in membership ||
        !membership.value ||
        membership.value.status !== 'active' ||
        membership.value.membershipVersion !== context.membershipVersion ||
        membership.value.role !== context.role)
    )
      return false;
    const collection = this.collections.get(context.collectionId)?.snapshot;
    return (
      !collection ||
      (!('error' in collection) &&
        Boolean(collection.value) &&
        collection.value?.lifecycle !== 'deleted' &&
        collection.value?.lifecycle !== 'deleting')
    );
  }

  private entry(context: CapacityContext) {
    const key = JSON.stringify([
      context.collectionId,
      context.membershipVersion,
      context.role,
    ]);
    let entry = this.capacities.get(key);
    if (!entry) {
      entry = {
        context,
        snapshot: INACTIVE_CAPACITY_SNAPSHOT,
        listeners: new Set(),
        generation: 0,
        expiresAt: 0,
        dirty: false,
      };
      this.capacities.set(key, entry);
    }
    return entry;
  }

  private publish(
    entry: CapacityEntry,
    state: CollectionCapacityCapabilityState,
    preserveDisplay = false,
  ) {
    const displayState =
      preserveDisplay && entry.snapshot.displayState.status === 'ready'
        ? entry.snapshot.displayState
        : state;
    entry.snapshot = { state, displayState };
    entry.listeners.forEach((listener) => listener());
  }

  private fence(entry: CapacityEntry) {
    entry.generation += 1;
    entry.expiresAt = 0;
    entry.dirty = false;
    clearTimeout(entry.timer);
    this.publish(entry, inactive);
  }

  getCapacitySnapshot = (context: CapacityContext) =>
    this.disposed ? INACTIVE_CAPACITY_SNAPSHOT : this.entry(context).snapshot;

  subscribeCapacity = (context: CapacityContext, listener: () => void) => {
    if (this.disposed) return () => undefined;
    const entry = this.entry(context);
    entry.listeners.add(listener);
    this.updateVisibilityListeners();
    if (!this.allowed(context)) this.fence(entry);
    else if (entry.expiresAt <= Date.now()) void this.refreshEntry(entry);
    else this.scheduleExpiry(entry);
    return () => {
      entry.listeners.delete(listener);
      if (!entry.listeners.size) clearTimeout(entry.timer);
      this.updateVisibilityListeners();
    };
  };

  refreshCapacity = (context: CapacityContext) =>
    this.refreshEntry(this.entry(context));
  invalidateCapacity = (context: CapacityContext) => {
    if (this.disposed) return;
    const entry = this.entry(context);
    entry.expiresAt = 0;
    clearTimeout(entry.timer);
    if (!this.allowed(context)) {
      this.fence(entry);
      return;
    }
    // A mutation during a request needs one trailing read; discard its old response.
    if (entry.pending) entry.dirty = true;
    this.publish(entry, loading, true);
    if (entry.listeners.size) void this.refreshEntry(entry);
  };

  private refreshEntry(entry: CapacityEntry): Promise<void> {
    if (!this.allowed(entry.context)) {
      this.fence(entry);
      return Promise.resolve();
    }
    if (entry.pending) return entry.pending;
    clearTimeout(entry.timer);
    const generation = entry.generation;
    this.publish(entry, loading, true);
    entry.pending = Promise.resolve()
      .then(async () => {
        if (this.disposed || entry.generation !== generation) return;
        try {
          const value = parseCollectionCapacityCapabilityResult(
            await this.dependencies.capacity(entry.context),
          );
          if (this.disposed || generation !== entry.generation || entry.dirty)
            return;
          if (
            value.collectionId !== entry.context.collectionId ||
            value.membershipVersion !== entry.context.membershipVersion ||
            value.audience !==
              (entry.context.role === 'administrator'
                ? 'editor'
                : entry.context.role)
          ) {
            throw new Error('Capacity response does not match its audience.');
          }
          entry.expiresAt = Date.now() + CAPACITY_CAPABILITY_REFRESH_MS;
          this.publish(entry, { status: 'ready', value });
        } catch {
          if (this.disposed || generation !== entry.generation || entry.dirty)
            return;
          entry.expiresAt = 0;
          this.publish(entry, error);
        }
      })
      .finally(() => {
        entry.pending = undefined;
        if (this.disposed) return;
        if (entry.dirty || generation !== entry.generation) {
          entry.dirty = false;
          if (entry.listeners.size && this.allowed(entry.context))
            void this.refreshEntry(entry);
        } else this.scheduleExpiry(entry);
      });
    return entry.pending;
  }

  private scheduleExpiry(entry: CapacityEntry) {
    clearTimeout(entry.timer);
    if (this.disposed || !entry.listeners.size || !this.allowed(entry.context))
      return;
    entry.timer = setTimeout(
      () => {
        // Expired denials must not continue blocking queued captures.
        this.publish(entry, error);
        if (document.visibilityState === 'visible')
          void this.refreshEntry(entry);
      },
      entry.expiresAt > Date.now()
        ? entry.expiresAt - Date.now()
        : CAPACITY_CAPABILITY_REFRESH_MS,
    );
  }

  private onVisible = () => {
    this.inventory.resume();
    if (
      document.visibilityState !== 'visible' ||
      this.visibilityTimer !== undefined
    )
      return;
    // Focus and visibilitychange commonly arrive together; share one refresh.
    this.capacities.forEach((entry) => {
      if (
        entry.listeners.size &&
        this.allowed(entry.context) &&
        entry.expiresAt <= Date.now()
      )
        void this.refreshEntry(entry);
    });
    if (navigator.onLine)
      this.inventory.refreshActive(INVENTORY_FOCUS_COOLDOWN_MS);
    this.visibilityTimer = setTimeout(() => {
      this.visibilityTimer = undefined;
    }, 250);
  };

  private onConnectionChanged = () => {
    this.inventory.resume();
    if (navigator.onLine && document.visibilityState === 'visible')
      this.inventory.refreshActive(0);
  };

  private updateVisibilityListeners() {
    const pollInventory = !this.disposed && this.inventory.hasConsumers;
    if (pollInventory && this.inventoryTimer === undefined) {
      this.inventoryTimer = setInterval(() => {
        if (navigator.onLine && document.visibilityState === 'visible')
          this.inventory.refreshActive(0);
      }, INVENTORY_RECONCILE_MS);
    } else if (!pollInventory && this.inventoryTimer !== undefined) {
      clearInterval(this.inventoryTimer);
      this.inventoryTimer = undefined;
    }
    const needed =
      !this.disposed &&
      (this.inventory.hasConsumers ||
        [...this.capacities.values()].some(
          (entry) => entry.listeners.size > 0,
        ));
    if (needed === this.listeningForVisibility) return;
    this.listeningForVisibility = needed;
    if (needed) {
      window.addEventListener('focus', this.onVisible);
      window.addEventListener('online', this.onConnectionChanged);
      window.addEventListener('offline', this.onConnectionChanged);
      document.addEventListener('visibilitychange', this.onVisible);
    } else {
      window.removeEventListener('focus', this.onVisible);
      window.removeEventListener('online', this.onConnectionChanged);
      window.removeEventListener('offline', this.onConnectionChanged);
      document.removeEventListener('visibilitychange', this.onVisible);
      clearTimeout(this.visibilityTimer);
      this.visibilityTimer = undefined;
    }
  }

  dispose() {
    this.disposed = true;
    this.inventory.dispose();
    this.memberships.forEach((source) => source.stop?.());
    this.collections.forEach((source) => source.stop?.());
    this.memberships.clear();
    this.collections.clear();
    this.capacities.forEach((entry) => this.fence(entry));
    this.updateVisibilityListeners();
  }
}
